import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import MaterialRequestPanel from "@/components/MaterialRequestPanel";
import NotificationsPanel from "@/components/NotificationsPanel";
import MenuViewer from "@/components/MenuViewer";
import HistoryStamps from "@/components/HistoryStamps";
import PrepSheetButton from "@/components/PrepSheetButton";
import { fmtDay, todayStr } from "@/lib/utils";
import { AlertTriangle, Clock, Truck, CheckCircle2, Flame, ClipboardList, Scale, PackageSearch, Loader2, Bell, Tag, BookOpen } from "lucide-react";
import { printPackingLabels } from "@/components/PackingLabels";
import { toast } from "sonner";

export default function KitchenView() {
  const { user } = useAuth();
  const [base, setBase] = useState(user?.location && ["MIHAN", "MANEWADA"].includes(user.location) ? user.location : "MIHAN");
  const fixedBase = user?.location && ["MIHAN", "MANEWADA"].includes(user.location);
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState({ totals: [], pending_orders: 0 });
  const [date, setDate] = useState(todayStr());

  const load = async () => {
    try {
      const dateParam = date ? { delivery_date: date } : {};
      const params = fixedBase ? { ...dateParam } : { base_kitchen: base, ...dateParam };
      const [o, t] = await Promise.all([
        api.get("/orders", { params }),
        api.get("/kitchen/cook-totals", { params: { base_kitchen: fixedBase ? user.location : base, ...dateParam } }),
      ]);
      setOrders(o.data);
      setTotals(t.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [base, date]); // eslint-disable-line

  const activeBase = fixedBase ? user.location : base;
  const [allCafs, setAllCafs] = useState([]);
  useEffect(() => {
    api.get("/cafeterias").then((r) => setAllCafs(r.data.filter((c) => c.active !== false))).catch(() => {});
  }, []);
  const kitchenCafs = allCafs.filter((c) => c.default_base_kitchen === activeBase);

  const [dispatchOrder, setDispatchOrder] = useState(null);
  const [dispatchQty, setDispatchQty] = useState({});
  const [busy, setBusy] = useState(false);

  const openDispatch = (o) => {
    setDispatchOrder(o);
    setDispatchQty(Object.fromEntries(o.items.map((it) => [it.item_id, it.qty])));
  };

  const confirmDispatch = async () => {
    setBusy(true);
    try {
      await api.patch(`/orders/${dispatchOrder.id}/dispatch`, {
        items: dispatchOrder.items.map((it) => ({ item_id: it.item_id, qty: Number(dispatchQty[it.item_id] || 0) })),
      });
      toast.success("Order dispatched");
      setDispatchOrder(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const lateInfo = (o) => {
    if (o.status !== "Preparing" || !o.edit_deadline) return null;
    const mins = Math.round((Date.parse(o.edit_deadline) + 2 * 3600000 - Date.now()) / 60000);
    if (mins >= 60) return null;
    return mins < 0 ? `Overdue by ${-mins} min` : `Due in ${mins} min`;
  };

  const sorted = useMemo(
    () => {
      const rank = (o) => (lateInfo(o) ? 0 : o.status === "Preparing" ? 1 : 2);
      const due = (o) => (o.edit_deadline ? Date.parse(o.edit_deadline) : Infinity);
      return [...orders].sort((a, b) => rank(a) - rank(b) || (rank(a) === 0 ? due(a) - due(b) : 0));
    },
    [orders] // eslint-disable-line
  );

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-primary" />
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Kitchen Display</h1>
            </div>
            <p className="text-muted-foreground">Incoming orders &amp; batch cook totals.</p>
          </div>
          {!fixedBase && (
            <div className="flex gap-2">
              {["MIHAN", "MANEWADA"].map((bk) => (
                <Button
                  key={bk}
                  variant={base === bk ? "default" : "outline"}
                  onClick={() => setBase(bk)}
                  data-testid={`kitchen-filter-${bk.toLowerCase()}`}
                >
                  {bk}
                </Button>
              ))}
            </div>
          )}
          {fixedBase && <Badge className="h-9 px-4 text-sm">{user.location} Base</Badge>}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="kitchen-date-filter">
          <span className="text-sm font-medium text-muted-foreground">Delivery date:</span>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44 font-mono" data-testid="kitchen-date-input" />
          <Button size="sm" variant={date === todayStr() ? "default" : "outline"} onClick={() => setDate(todayStr())} data-testid="kitchen-date-today-button">Today</Button>
          <Button size="sm" variant={!date ? "default" : "outline"} onClick={() => setDate("")} data-testid="kitchen-date-all-button">All dates</Button>
          <span className="text-sm text-muted-foreground" data-testid="kitchen-date-label">{date ? `Showing ${fmtDay(date)}` : "Showing all dates"}</span>
          <Button size="sm" variant="outline" className="gap-1.5 ml-auto" onClick={() => printPackingLabels(sorted.filter((o) => o.status === "Preparing"))} data-testid="kitchen-print-all-labels-button">
            <Tag className="h-4 w-4" /> Print All Labels
          </Button>
        </div>

        <Tabs defaultValue="tickets">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="tickets" data-testid="kitchen-tab-tickets" className="gap-2">
              <ClipboardList className="h-4 w-4" /> Active Orders
            </TabsTrigger>
            <TabsTrigger value="cook" data-testid="kitchen-tab-cook-totals" className="gap-2">
              <Scale className="h-4 w-4" /> Cook Totals
            </TabsTrigger>
            <TabsTrigger value="materials" data-testid="kitchen-tab-materials" className="gap-2">
              <PackageSearch className="h-4 w-4" /> Request Material
            </TabsTrigger>
            <TabsTrigger value="menu" data-testid="kitchen-tab-menu" className="gap-2">
              <BookOpen className="h-4 w-4" /> Menu
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="kitchen-tab-notifications" className="gap-2">
              <Bell className="h-4 w-4" /> Notifications
            </TabsTrigger>
          </TabsList>

          {/* Tickets */}
          <TabsContent value="tickets" className="mt-6">
            {sorted.length === 0 && (
              <Card className="p-10 text-center text-muted-foreground">No orders for {activeBase}{date ? ` on ${fmtDay(date)}` : ""}.</Card>
            )}
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 mobile-compact" data-testid="kitchen-tickets-grid">
              {sorted.map((o) => {
                const late = lateInfo(o);
                return (
                <Card key={o.id} className={`p-4 fade-up flex flex-col ${late ? "border-2 border-red-500 bg-red-50 dark:bg-red-950/40" : "border-orange-100 dark:border-slate-800"}`} data-testid={late ? "kitchen-ticket-card-late" : "kitchen-ticket-card"}>
                  {late && (
                    <div className="flex items-center gap-1.5 -mt-1 mb-2 text-xs font-bold uppercase tracking-wider text-red-700 dark:text-red-300" data-testid="kitchen-ticket-late-alert">
                      <AlertTriangle className="h-4 w-4 animate-pulse" /> {late}
                    </div>
                  )}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold leading-tight">{o.cafeteria_name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Clock className="h-3 w-3" />
                        {new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                      </p>
                    </div>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="flex gap-1.5 mb-3">
                    <Badge variant="secondary">{o.base_kitchen}</Badge>
                    <Badge variant="outline" className="border-primary/40 text-primary" data-testid="kitchen-ticket-delivery-badge">Deliver {o.delivery_date ? `${fmtDay(o.delivery_date)} · ` : ""}{o.delivery_time}{o.delivery_exact_time ? ` · ${o.delivery_exact_time}` : ""}</Badge>
                  </div>
                  <ul className="text-sm space-y-1 mb-3 flex-1">
                    {o.status !== "Preparing" && (
                      <li className="grid grid-cols-[1fr_auto_auto_auto] gap-2 text-[10px] uppercase font-mono tracking-wider text-muted-foreground pb-0.5 border-b border-border">
                        <span>Item</span><span className="text-right">Ord</span><span className="text-right">Disp</span><span className="text-right">Rcvd</span>
                      </li>
                    )}
                    {o.items.map((it, i) => (
                      o.status === "Preparing" ? (
                        <li key={i} className="flex justify-between border-b border-dashed border-border/60 pb-0.5">
                          <span>{it.item_name}</span>
                          <span className="font-mono font-semibold">{it.qty} {it.unit}</span>
                        </li>
                      ) : (
                        <li key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                          <span className="truncate">{it.item_name}</span>
                          <span className="font-mono text-muted-foreground text-right">{it.qty}</span>
                          <span className="font-mono font-semibold text-right">{it.dispatched_qty ?? 0}</span>
                          <span className="font-mono text-right">{o.status === "Delivered" ? (it.received_qty ?? 0) : "—"}</span>
                        </li>
                      )
                    ))}
                  </ul>
                  {o.remarks && <p className="text-xs italic text-amber-700 dark:text-amber-400 mb-2">“{o.remarks}”</p>}
                  {o.received_comment && <p className="text-xs text-rose-700 dark:text-rose-300 mb-3 border-t pt-2"><span className="font-semibold">Cafeteria note:</span> “{o.received_comment}”</p>}
                  <div className="mb-3">
                    <HistoryStamps history={o.history} fallbackAction="Placed" fallbackName={o.placed_by_name} fallbackAt={o.created_at} testid="kitchen-ticket-history" />
                  </div>
                  <div className="flex gap-2">
                    {o.status === "Preparing" && (
                      <>
                        <Button size="sm" variant="outline" className="gap-1.5" data-testid="kitchen-print-label-button" onClick={() => printPackingLabels([o])}>
                          <Tag className="h-4 w-4" /> Label
                        </Button>
                        <Button size="sm" className="flex-1 gap-1.5" data-testid="kitchen-mark-dispatched-button" onClick={() => openDispatch(o)}>
                          <Truck className="h-4 w-4" /> Dispatch
                        </Button>
                      </>
                    )}
                    {o.status === "Dispatched" && (
                      <p className="text-sm text-blue-600 font-medium flex items-center gap-1.5 w-full justify-center py-1.5">
                        <Truck className="h-4 w-4" /> Dispatched · awaiting cafeteria receipt
                      </p>
                    )}
                    {o.status === "Delivered" && (
                      <p className="text-sm text-emerald-600 font-medium flex items-center gap-1.5 w-full justify-center py-1.5">
                        <CheckCircle2 className="h-4 w-4" /> Received by cafeteria
                      </p>
                    )}
                  </div>
                </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Cook totals */}
          <TabsContent value="cook" className="mt-6">
            <Card className="p-6 border-orange-100 dark:border-slate-800">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div>
                  <h3 className="font-heading text-xl font-bold">Batch Cook Totals — {activeBase}{date ? ` · ${fmtDay(date)}` : ""}</h3>
                  <p className="text-sm text-muted-foreground">Aggregated quantity to cook across all pending orders.</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="h-8">{totals.pending_orders} pending</Badge>
                  <PrepSheetButton base={activeBase} date={date} totals={totals.totals} orders={orders} />
                </div>
              </div>
              {totals.totals.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Nothing pending to cook right now.</p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {totals.totals.map((t, i) => (
                    <div key={i} className="rounded-xl border border-border bg-secondary/50 p-4 fade-up" data-testid="cook-totals-item-row">
                      <p className="font-medium text-sm mb-1">{t.item_name}</p>
                      <p className="font-mono text-2xl font-extrabold text-primary" data-testid="cook-totals-aggregate-qty">
                        {t.total_qty} <span className="text-base text-muted-foreground">{t.unit}</span>
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Material requests */}
          <TabsContent value="materials" className="mt-6">
            <MaterialRequestPanel />
          </TabsContent>

          <TabsContent value="menu" className="mt-6">
            <MenuViewer cafeterias={kitchenCafs} title={`${activeBase} kitchen`} />
          </TabsContent>

          <TabsContent value="notifications" className="mt-6">
            <NotificationsPanel />
          </TabsContent>
        </Tabs>
      </main>

      {/* Dispatch dialog */}
      <Dialog open={!!dispatchOrder} onOpenChange={(v) => !v && setDispatchOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dispatch Order — {dispatchOrder?.cafeteria_name}</DialogTitle>
            <DialogDescription>Enter how much of each item you are dispatching. You can send less than ordered if not fully cooked.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            <div className="grid grid-cols-[1fr_auto_auto] gap-2 text-[10px] uppercase font-mono tracking-wider text-muted-foreground pb-1 border-b border-border sticky top-0 bg-card">
              <span>Item</span><span className="text-right">Ordered</span><span className="text-right w-24">Dispatch Qty</span>
            </div>
            {dispatchOrder?.items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center py-1" data-testid="kitchen-dispatch-item-row">
                <span className="truncate text-sm">{it.item_name} <span className="text-muted-foreground">({it.unit})</span></span>
                <span className="font-mono text-sm text-muted-foreground text-right">{it.qty}</span>
                <Input
                  type="number" min="0" max={it.qty} step="0.5"
                  className="h-8 w-24 text-right font-mono"
                  data-testid="kitchen-dispatch-qty-input"
                  value={dispatchQty[it.item_id] ?? ""}
                  onChange={(e) => setDispatchQty({ ...dispatchQty, [it.item_id]: e.target.value })}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={confirmDispatch} disabled={busy} className="gap-2" data-testid="kitchen-dispatch-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />} Confirm Dispatch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
